import type { ReactElement } from "react";
import { cloneElement, useCallback, useEffect, useMemo, useRef } from "react";

import { TransitionGroup } from "react-transition-group";

import { usePrevious } from "app/components/hooks/usePrevious";
import { useDidUpdate } from "app/custom-hooks/useDidUpdate";

import type { GetColumnPluginsForSlot } from "../../plugins/types";
import type { SearchableSelectionOption } from "../../types";
import type { ListIndicator } from "../AddedItemIndicator/AddedItemIndicator";
import { AddedItemIndicator } from "../AddedItemIndicator/AddedItemIndicator";
import { NotSelectedColumnOptionFadeTransition } from "../ColumnOptionFadeTransition/NotSelectedColumnOptionFadeTransition";
import { EmptyColumnOptionListMessage } from "../EmptyColumnOptionListMessage/EmptyColumnOptionListMessage";

import { diffEnteringIds } from "./enteringIds";
import { NotSelectedColumnOptionRow } from "./NotSelectedColumnOptionRow";
import { pickIndicator } from "./pickIndicator";
import {
    StyledColumnOptionsList,
    StyledColumnOptionsViewport,
    StyledColumnOptionsVirtualTrack,
    StyledColumnOptionsVirtualWrapper,
} from "./styles";
import type { UniformRowVirtualizer } from "./useUniformRowVirtualizer";
import { useUniformRowVirtualizer } from "./useUniformRowVirtualizer";

type ColumnOptionsTrackProps<O> = {
    virtualizer: UniformRowVirtualizer;
    searchTerm: string;
    items: readonly O[];
    isLayoutAnimating: boolean;
    getColumnPluginsForSlot?: GetColumnPluginsForSlot;
    onColumnOptionClick?: (columnName: string) => void;
};

const ColumnOptionsTrack = <O extends SearchableSelectionOption>(props: ColumnOptionsTrackProps<O>) => {
    const { virtualizer, searchTerm, items, isLayoutAnimating, getColumnPluginsForSlot } = props;

    const itemIds = useMemo(() => items.map((item) => item.id), [items]);
    const itemIdsSet = useMemo(() => new Set(itemIds), [itemIds]);
    const prevItemIdsSet = usePrevious(itemIdsSet);
    const addedColumnNamesRef = useRef<Set<string>>(new Set());
    const removedColumnNamesRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        const visibleIndexes = new Set(virtualizer.getVirtualIndexes());

        for (const id of diffEnteringIds(prevItemIdsSet, itemIds, visibleIndexes)) {
            addedColumnNamesRef.current.add(id);
        }
    }, [itemIdsSet]);

    const childFactory = (child: ReactElement<{ optionName: string; exit: boolean }>) => {
        if (removedColumnNamesRef.current.has(child.props.optionName)) {
            return cloneElement(child, { exit: true });
        }

        return child;
    };

    const { onColumnOptionClick } = props;

    // Stable, so the rows below can memoise. They bind their own id rather than being handed a
    // per-row closure, which would change identity on every render regardless.
    const handleColumnOptionClick = useCallback(
        (columnName: string) => {
            removedColumnNamesRef.current.add(columnName);
            onColumnOptionClick?.(columnName);
        },
        [onColumnOptionClick],
    );

    return (
        <TransitionGroup
            component={StyledColumnOptionsVirtualTrack}
            style={{ height: `${virtualizer.getTotalSize()}px` }}
            data-layout-animation={isLayoutAnimating ? "" : undefined}
            childFactory={childFactory}
        >
            {virtualizer.getVirtualItems().map((virtualItem) => {
                const column = items[virtualItem.index];

                return (
                    <NotSelectedColumnOptionFadeTransition
                        key={column.id}
                        enter={addedColumnNamesRef.current.has(column.id)}
                        exit={false}
                        onEntered={() => addedColumnNamesRef.current.delete(column.id)}
                        onExited={() => removedColumnNamesRef.current.delete(column.id)}
                        optionName={column.id}
                        style={{ transform: `translateY(${virtualItem.start}px)` }}
                    >
                        <NotSelectedColumnOptionRow
                            column={column}
                            searchTerm={searchTerm}
                            getColumnPluginsForSlot={getColumnPluginsForSlot}
                            onSelect={handleColumnOptionClick}
                        />
                    </NotSelectedColumnOptionFadeTransition>
                );
            })}
        </TransitionGroup>
    );
};

/**
 * A column that has just returned to this list, plus a counter. The counter is what makes the
 * indicator fire every time: without it, restoring the same column twice in a row leaves the value
 * unchanged and the effect never re-runs.
 */
export type RestoredColumnToken = { id: string; seq: number };

type NotSelectedColumnOptionList<O> = {
    columnOptions: readonly O[];
    searchTerm: string;
    /** Identifies the active search + category. Changing it evicts rows mid-exit-animation. */
    filterKey: string;
    isLayoutAnimating: boolean;
    restoredColumn: RestoredColumnToken | null;
    getColumnPluginsForSlot?: GetColumnPluginsForSlot;
    onColumnOptionClick: (columnName: string) => void;
};

export const NotSelectedColumnOptionsList = <O extends SearchableSelectionOption>(
    props: NotSelectedColumnOptionList<O>,
) => {
    const {
        columnOptions,
        onColumnOptionClick,
        searchTerm,
        filterKey,
        isLayoutAnimating,
        restoredColumn,
        getColumnPluginsForSlot,
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const topIndicatorRef = useRef<ListIndicator>(null);
    const bottomIndicatorRef = useRef<ListIndicator>(null);

    const getScrollElement = useCallback(() => containerRef.current, []);
    // Keying by id rather than index keeps a row's identity across filtering, and keeps the
    // virtualiser from rebuilding every measurement on each scroll tick.
    const getItemKey = useCallback((index: number) => columnOptions[index].id, [columnOptions]);

    const rowVirtualizer = useUniformRowVirtualizer({
        count: columnOptions.length,
        getScrollElement,
        getItemKey,
    });

    // Reset on the *committed* filter, not on the keystroke. Scrolling in the change handler would
    // snap the previous result set to the top before the new one renders. `scrollToOffset` rather
    // than a raw `scrollTo`, so the virtualiser's own offset moves with the element.
    useDidUpdate(() => rowVirtualizer.scrollToOffset(0), [filterKey]);

    useDidUpdate(() => {
        if (!restoredColumn) {
            return;
        }

        const index = columnOptions.findIndex((option) => option.id === restoredColumn.id);
        const indicator = pickIndicator(index, rowVirtualizer.range);

        if (indicator === "top") {
            topIndicatorRef.current?.appear();
        } else if (indicator === "bottom") {
            bottomIndicatorRef.current?.appear();
        }
    }, [restoredColumn]);

    const getContent = () => {
        if (columnOptions.length === 0) {
            return <EmptyColumnOptionListMessage />;
        }

        return (
            <StyledColumnOptionsVirtualWrapper>
                <ColumnOptionsTrack
                    key={filterKey}
                    virtualizer={rowVirtualizer}
                    items={columnOptions}
                    isLayoutAnimating={isLayoutAnimating}
                    onColumnOptionClick={onColumnOptionClick}
                    searchTerm={searchTerm}
                    getColumnPluginsForSlot={getColumnPluginsForSlot}
                />
            </StyledColumnOptionsVirtualWrapper>
        );
    };

    return (
        <StyledColumnOptionsViewport>
            <AddedItemIndicator
                position="top"
                listIndicatorRef={topIndicatorRef}
            />
            <StyledColumnOptionsList
                ref={containerRef}
                data-automation="column-picker-not-selected-list"
            >
                {getContent()}
            </StyledColumnOptionsList>
            <AddedItemIndicator
                position="bottom"
                listIndicatorRef={bottomIndicatorRef}
            />
        </StyledColumnOptionsViewport>
    );
};
